import Modal from "./Modal";
import useSupportSeekerModal from '../../hooks/post/useSupportSeekerModal';
import { useGetPostBankDetailQuery } from "../../services/postApis";
import { useTranslation } from "react-i18next";
import FileUploader from "../../components/FileUploader";
import { NavLink } from "react-router-dom";

const SupportSeekerModal = ({ slug, isDonor }) => {
  const supportSeekerModal = useSupportSeekerModal();
  const { data, isLoading } = useGetPostBankDetailQuery(slug, {
    skip: !supportSeekerModal.isOpen || !isDonor,
  });

  const { t } = useTranslation("post");
  const translation = t("post-support");

  // Reusable card for information display
  const InfoCard = ({ children }) => (
    <div className="rounded-2xl border border-green-800 dark:border-green-100 bg-green-100 dark:bg-green-900 shadow-md p-6 space-y-4">
      <dl className="space-y-3 text-sm sm:text-base">{children}</dl>
    </div>
  );

  // Row inside the info card
  const InfoRow = ({ label, value }) => (
    <div className="flex justify-between items-start gap-4">
      <dt className="font-medium text-gray-600 dark:text-gray-400">{label}</dt>
      <dd className="text-right text-gray-900 dark:text-white break-words max-w-[65%]">{value}</dd>
    </div>
  );

  // Modal content
  const content = (
    <div className="space-y-8">

      <div className="space-y-2">
        {/* Instructions */}
        <h2 className="text-xl font-semibold">{translation.guidelines}</h2>

        <section className="space-y-4 bg-green-100 dark:bg-green-800 p-5 rounded-2xl border border-gray-200 dark:border-green-100 shadow-sm">
          {[
            translation.first_desc,
            translation.second_desc,
            translation.third_desc,
          ].map((desc, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 font-semibold text-sm">
                  {idx + 1}
                </span>
              </div>
              <p className="text-base text-slate-900 dark:text-stone-200 leading-relaxed">
                {desc}
              </p>
            </div>
          ))}
        </section>

      </div>

      {/* Bank Details or Identity Info */}
      <section className="space-y-4  text-slate-950 dark:text-stone-200">
        <h2 className="text-xl font-semibold">{translation.seeker_bank}</h2>

        {!isDonor ? (
          <p className="text-sm italic text-gray-600 dark:text-gray-400">
            {translation.only_donor} <NavLink to="/donor-community" className="text-blue-600 hover:underline dark:text-blue-400">
            {translation.join_now}
          </NavLink>
          </p>
        ) : isLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {translation.loading_banking_details}
          </p>
        ) : data?.account_holder ? (
          <InfoCard>
            <InfoRow label={translation.account_holder} value={data.account_holder} />
            <InfoRow label={translation.bank_name} value={data.bank_name} />
            <InfoRow label={translation.account_number} value={data.account_number} />
            <InfoRow label={translation.country} value={data.country} />
            <InfoRow label={translation.cnic} value={data.cnic} />
            <InfoRow label={translation.phone_number} value={data.phone_number} />

          </InfoCard>
        ) : data?.name ? (
          <InfoCard>
            <InfoRow label={translation.seeker_name} value={data.name} />
            <InfoRow label={translation.country} value={data.country} />
            <InfoRow
              label={translation.phone_number}
              value={`${data.country_code} ${data.phone_number}`}
            />
            <InfoRow label={translation.cnic} value={data.cnic} />
            
            <p className="border rounded-xl bg-green-200 p-3 border-dotted border-black font-medium dark:bg-green-600 dark:text-black">
              {translation.cnic_transfer_message}
            </p>
          </InfoCard>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">{translation.seeker_no_bank}</p>
        )}
      </section>

      {/* Upload Proof Section */}
      {isDonor && (
        <section className="space-y-4 text-slate-950 dark:text-stone-200">
          <label className="block text-base font-semibold dark:text-white ">
            {translation.payment_proof}
            <span className="ml-2 text-xs text-gray-500 mx-2">
              ({translation.amount_message})
            </span>
          </label>

          <FileUploader
            fileNameInDB="payment_proof"
            allowedTypes={["jpeg", "png", 'pdf', 'jpg', "bmp", "webp", "heic", "heif"]}
            generateUrlEndpoint={`${process.env.REACT_APP_BACKEND_DOMAIN}/post/generate-presigned-url/`}
            updateBackendEndpoint={`${process.env.REACT_APP_BACKEND_DOMAIN}/post/donate-post/`}
            additionalFormData={{ slug }}
            maxSizeMB={100}
            awsUploadDestination="donation-proofs/username"
            description={translation.description}
          />
        </section>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={supportSeekerModal.isOpen}
      close={supportSeekerModal.close}
      label={translation.name}
      content={content}
    />
  );
};

export default SupportSeekerModal;
